import { Component, OnInit, PLATFORM_ID, inject, ChangeDetectionStrategy } from '@angular/core';
import { isPlatformServer } from '@angular/common';

import { Store } from '@ngrx/store';
import { EMPTY, Observable, filter, map, switchMap, tap } from 'rxjs';

import { PopoverOptions } from '@fcom/ui-components/components/popover/popover.interface';
import { ButtonSize } from '@fcom/ui-components/components/buttons/button/enums';
import { PopoverPosition } from '@fcom/ui-components/components/popover/popover.enum';
import { AppState, ConfigService, StorageService } from '@fcom/core';
import { LoginStatus, LoginStep } from '@fcom/core-api/login/interfaces/login.interface';
import { loginStatus } from '@fcom/core/selectors/login.selector';
import { PersonalizationTrackingService } from '@fcom/loyalty-personalization/services/personalization-tracking.service';
import { finShare } from '@fcom/rx';
import * as LoginActions from '@fcom/core/actions/login.actions';

import { GtmService } from '../../gtm/services/gtm.service';
import { ElementActions, ElementTypes, GaContext } from '../../interfaces/gtm.interface';
import {
  WelcomeBackMessageService,
  WELCOME_BACK_MESSAGE_CLOSED_KEY,
} from '../../services/welcome-back-message/welcome-back-message.service';
import { JoinService } from '../../login/services/join.service';
import { LoginService } from '../../login/services/login.service';

enum MessageType {
  FPLUS_MEMBER = 'fplus-member',
  ANONYMOUS = 'anonymous',
}

const TRACKING_ELEMENT_NAME = 'welcome-back-message';

@Component({
  selector: 'fin-welcome-back-message',
  templateUrl: './welcome-back-message.component.html',
  styleUrls: ['./welcome-back-message.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class WelcomeBackMessageComponent implements OnInit {
  private store$ = inject<Store<AppState>>(Store);
  private storageService = inject(StorageService);
  private loginService = inject(LoginService);
  private gtmService = inject(GtmService);
  joinService = inject(JoinService);
  private personalizationTrackingService = inject(PersonalizationTrackingService);
  private configService = inject(ConfigService);
  private welcomeBackMessageService = inject(WelcomeBackMessageService);
  private platform = inject(PLATFORM_ID);

  readonly MessageType = MessageType;

  readonly ButtonSize = ButtonSize;

  popoverOptions: PopoverOptions = {
    popoverID: 'welcome-back-message',
    openByDefault: true,
    showCloseButton: true,
    closeOnScroll: false,
    showArrowCaret: false,
    showLeftBorder: false,
    popoverPosition: PopoverPosition.BOTTOM,
    roundedCorners: true,
    useFixedPosition: true,
  };

  messageType$: Observable<MessageType>;

  ngOnInit(): void {
    if (this.storageService.SESSION.get(WELCOME_BACK_MESSAGE_CLOSED_KEY) || isPlatformServer(this.platform)) {
      this.messageType$ = EMPTY;
    } else {
      this.messageType$ = this.store$.pipe(
        loginStatus(),
        filter((status) => status === LoginStatus.NOT_LOGGED_IN),
        tap(() => this.trackGtm('view', ElementActions.VIEW)),
        switchMap(() =>
          this.personalizationTrackingService
            .isNotLoggedInFPlusMember()
            .pipe(
              map((isNotLoggedInFPlusMember) =>
                isNotLoggedInFPlusMember ? MessageType.FPLUS_MEMBER : MessageType.ANONYMOUS
              )
            )
        ),
        finShare()
      );
    }
  }

  onLoginButtonClick(): void {
    this.trackGtm('login', ElementActions.CLICK);
    if (this.configService.cfg.enableLoginDialog) {
      this.store$.dispatch(LoginActions.setLoginPhaseStep({ step: LoginStep.CREDENTIALS }));
    } else {
      this.loginService.redirectToLogin();
    }
  }

  onJoinButtonClick(): void {
    this.trackGtm('join', ElementActions.CLICK);
    if (this.configService.cfg.enableJoinDialog) {
      this.joinService.joinDialogOpen$.next(true);
    } else {
      this.loginService.redirectToJoin();
    }
  }

  onCloseClick(): void {
    this.trackGtm('close', ElementActions.CLOSE);
    this.welcomeBackMessageService.close();
  }

  private trackGtm(eventName: string, action: ElementActions): void {
    this.gtmService.trackElement(TRACKING_ELEMENT_NAME, GaContext.FINNAIR_PLUS, ElementTypes.BANNER, eventName, action);
  }
}
